import api from './api'
import {
  AlteracaoGestao,
  CampanhaParticipante,
  CampanhaParticipanteRequest,
  CampanhaProducao,
  ConsultorElegivel,
} from '@/types/campanha'

const BASE_URL = '/api/v1/campanhas'

/**
 * Participantes, produção, elegíveis e trilha da campanha (A14, V43; AN-8, V56). O CRUD da
 * campanha segue em `campanhaService.ts`. Tudo aqui é `GESTAO_CAMPANHA_EQUIPE`: ADMIN, ou
 * GERENTE com GESTOR_EQUIPE (só a própria equipe — fora dela, 403 FORA_DA_EQUIPE).
 */
export const campanhasService = {
  listarParticipantes: async (campanhaId: string): Promise<CampanhaParticipante[]> => {
    return api.get<CampanhaParticipante[]>(`${BASE_URL}/${campanhaId}/participantes`)
  },

  /**
   * Só em audiência NOMINAL (dinâmica → 422 CAMPANHA_AUDIENCIA_DINAMICA). 409
   * PARTICIPANTE_DUPLICADO; 422 CONSULTOR_INVALIDO. Tenant/canal vêm da projeção.
   */
  inscrever: async (
    campanhaId: string,
    req: CampanhaParticipanteRequest
  ): Promise<CampanhaParticipante> => {
    return api.post<CampanhaParticipante>(`${BASE_URL}/${campanhaId}/participantes`, req)
  },

  /** Lote tudo ou nada (fora da equipe → 403 FORA_DA_EQUIPE); devolve os criados. */
  inscreverLote: async (
    campanhaId: string,
    participantes: CampanhaParticipanteRequest[]
  ): Promise<CampanhaParticipante[]> => {
    return api.post<CampanhaParticipante[]>(`${BASE_URL}/${campanhaId}/participantes/lote`, {
      participantes,
    })
  },

  /** Meta individual do participante (qualquer audiência); `null` volta à meta padrão. */
  atualizarMetaParticipante: async (
    campanhaId: string,
    consultorId: string,
    valorMeta: number | null
  ): Promise<CampanhaParticipante> => {
    return api.put<CampanhaParticipante>(`${BASE_URL}/${campanhaId}/participantes/${consultorId}`, {
      valorMeta,
    })
  },

  /** Só em audiência NOMINAL (dinâmica → 422 CAMPANHA_AUDIENCIA_DINAMICA). */
  remover: async (campanhaId: string, consultorId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${campanhaId}/participantes/${consultorId}`)
  },

  producao: async (campanhaId: string): Promise<CampanhaProducao> => {
    return api.get<CampanhaProducao>(`${BASE_URL}/${campanhaId}/producao`)
  },

  /**
   * Consultores ativos ainda não inscritos (ADMIN: todos; GERENTE+GESTOR: só a equipe), máx.
   * 200. Substitui `/v1/usuarios` (ADMIN-only no auth) na tela de campanhas.
   */
  consultoresElegiveis: async (campanhaId: string, busca?: string): Promise<ConsultorElegivel[]> => {
    const q = busca?.trim() ? `?busca=${encodeURIComponent(busca.trim())}` : ''
    return api.get<ConsultorElegivel[]>(`${BASE_URL}/${campanhaId}/consultores-elegiveis${q}`)
  },

  /** Trilha append-only (GERENTE: linhas da equipe + as da campanha). */
  alteracoes: async (campanhaId: string): Promise<AlteracaoGestao[]> => {
    return api.get<AlteracaoGestao[]>(`${BASE_URL}/${campanhaId}/alteracoes`)
  },
}

export default campanhasService
